import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { KGVersion } from '../../types';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDateTime } from '../../utils/dateUtils';
import CreateVersionModal from './CreateVersionModal';
import ConfirmModal from '../ConfirmModal';
import '../shared.css';
import './KGDetails.css';

const KGDetails: React.FC = () => {
  const { kgId } = useParams<{ kgId: string }>();
  const navigate = useNavigate();
  const [versions, setVersions] = useState<KGVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [editingDataVersionId, setEditingDataVersionId] = useState<string | null>(null);
  const [editedDataVersion, setEditedDataVersion] = useState('');
  const [dataVersionError, setDataVersionError] = useState<Record<string, string>>({});
  const [indexCounts, setIndexCounts] = useState<Record<string, number>>({});
  const [autoScalingToggling, setAutoScalingToggling] = useState<Record<string, boolean>>({});
  const [versionsWithInstances, setVersionsWithInstances] = useState<Set<string>>(new Set());
  const [confirmDeleteVersionId, setConfirmDeleteVersionId] = useState<string | null>(null);

  const loadIndexCounts = useCallback(async (kgIdArg: string, versionIds: string[]) => {
    const counts: Record<string, number> = {};
    await Promise.allSettled(versionIds.map(async (versionId) => {
      try {
        const res: any = await apiService.listESIndexes(kgIdArg, versionId);
        counts[versionId] = Array.isArray(res.data) ? res.data.length : 0;
      } catch {
        counts[versionId] = 0;
      }
    }));
    setIndexCounts(counts);
  }, []);

  const loadVersions = useCallback(async () => {
    if (!kgId) return;
    try {
      setLoading(true);
      setError('');
      const response: any = await apiService.listVersions(kgId);
      if (response.data) {
        const versionList = response.data.map((item: any) => ({
          kg_id: item.kg_id || kgId,
          version_id: item.version_id,
          kg_name: item.kg_name || item.attributes?.kg_name || 'Unnamed KG',
          kg_description: item.kg_description || item.attributes?.kg_description,
          created_at: item.created_at || item.attributes?.created_at,
          published: item.published ?? item.attributes?.published ?? false,
          org_id: item.org_id || item.attributes?.org_id,
          auto_scaling_enabled: item.auto_scaling_enabled ?? item.attributes?.auto_scaling_enabled ?? false,
          gds_enabled: item.gds_enabled ?? item.attributes?.gds_enabled ?? false,
          data_version: item.data_version || item.attributes?.data_version || undefined,
          data_files: item.data_files || item.attributes?.data_files,
        }));
        setVersions(versionList);
        loadIndexCounts(kgId, versionList.map((v: KGVersion) => v.version_id));
        loadInstanceCounts(kgId);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load versions'));
    } finally {
      setLoading(false);
    }
  }, [kgId, loadIndexCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInstanceCounts = useCallback(async (kgIdArg: string) => {
    try {
      const response: any = await apiService.getAllInstances();
      if (response.data) {
        const withInstances = new Set<string>(
          response.data
            .filter((inst: any) =>
              inst.kg_id === kgIdArg && inst.is_terminated !== true
            )
            .map((inst: any) => String(inst.version_id))
        );
        setVersionsWithInstances(withInstances);
      }
    } catch {
      // non-critical — autoscale toggle stays disabled on error
    }
  }, []);

  const handlePublishToggle = useCallback(async (versionId: string, currentPublished: boolean) => {
    if (!kgId) return;
    try {
      await apiService.publishVersion(kgId, versionId, !currentPublished);
      loadVersions();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to update version'));
    }
  }, [kgId, loadVersions]);

  const handleDeleteVersion = useCallback((versionId: string) => {
    setConfirmDeleteVersionId(versionId);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!kgId || !confirmDeleteVersionId) return;
    const versionId = confirmDeleteVersionId;
    setConfirmDeleteVersionId(null);
    try {
      await apiService.deleteVersion(kgId, versionId);
      loadVersions();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete version'));
    }
  }, [kgId, confirmDeleteVersionId, loadVersions]);

  const handleVersionCreated = useCallback(() => {
    setShowCreateModal(false);
    loadVersions();
  }, [loadVersions]);

  const handleStartEditDescription = useCallback((versionId: string, currentDescription: string) => {
    setEditingVersionId(versionId);
    setEditedDescription(currentDescription || '');
  }, []);

  const handleCancelEditDescription = useCallback(() => {
    setEditingVersionId(null);
    setEditedDescription('');
  }, []);

  const handleSaveDescription = useCallback(async (versionId: string) => {
    if (!kgId) return;
    try {
      await apiService.updateVersionDescription(kgId, versionId, editedDescription);
      setEditingVersionId(null);
      setEditedDescription('');
      loadVersions();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to update description'));
    }
  }, [kgId, editedDescription, loadVersions]);

  const handleStartEditDataVersion = useCallback((versionId: string, current: string) => {
    setEditingDataVersionId(versionId);
    setEditedDataVersion(current || '');
    setDataVersionError(prev => ({ ...prev, [versionId]: '' }));
  }, []);

  const handleCancelEditDataVersion = useCallback(() => {
    setEditingDataVersionId(null);
    setEditedDataVersion('');
  }, []);

  const handleSaveDataVersion = useCallback(async (versionId: string) => {
    if (!kgId) return;
    const trimmed = editedDataVersion.trim();
    if (!trimmed) {
      setDataVersionError(prev => ({ ...prev, [versionId]: 'Must be a non-empty string' }));
      return;
    }
    try {
      await apiService.updateVersionDataVersion(kgId, versionId, trimmed);
      setEditingDataVersionId(null);
      setEditedDataVersion('');
      setDataVersionError(prev => ({ ...prev, [versionId]: '' }));
      loadVersions();
    } catch (err: any) {
      const msg = extractErrorMessage(err, 'Failed to update data version');
      setDataVersionError(prev => ({ ...prev, [versionId]: msg }));
    }
  }, [kgId, editedDataVersion, loadVersions]);

  const handleAutoScalingToggle = useCallback(async (versionId: string, currentEnabled: boolean) => {
    if (!kgId) return;
    setAutoScalingToggling(prev => ({ ...prev, [versionId]: true }));
    try {
      await apiService.updateVersionAutoScaling(kgId, versionId, !currentEnabled);
      setVersions(prev => prev.map(v =>
        v.version_id === versionId ? { ...v, auto_scaling_enabled: !currentEnabled } : v
      ));
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to update auto scaling'));
    } finally {
      setAutoScalingToggling(prev => ({ ...prev, [versionId]: false }));
    }
  }, [kgId]);

  useEffect(() => {
    if (kgId) loadVersions();
  }, [kgId, loadVersions]);

  if (loading) return (
    <div className="kg-details-container">
      <div className="page-loader"><div className="page-spinner" /></div>
    </div>
  );

  const kgName = versions[0]?.kg_name || kgId;

  const filteredVersions = versions
    .filter((v) => {
      const s = searchTerm.toLowerCase();
      return v.version_id?.toString().toLowerCase().includes(s)
        || v.kg_description?.toLowerCase().includes(s)
        || (v.published ? 'published' : 'unpublished').includes(s);
    })
    .slice()
    .sort((a, b) => Number(b.version_id) - Number(a.version_id));

  const publishedCount   = versions.filter(v => v.published).length;
  const unpublishedCount = versions.length - publishedCount;

  return (
    <div className="kg-details-container">
      <button className="btn-back" onClick={() => navigate('/kg')}>← Back to KGs</button>

      <div className="kg-details-header">
        <div>
          <h1>{kgName}</h1>
          <p className="kg-id">ID: {kgId}</p>
        </div>
        <div className="header-actions">
          <input
            type="text"
            className="search-input"
            placeholder="Search versions"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="tab-btn tab-btn--primary" onClick={() => setShowCreateModal(true)}>
            Create New Version
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {versions.length > 0 && (
        <div className="kg-summary-bar">
          <span className="kg-summary-total">{versions.length} version{versions.length !== 1 ? 's' : ''}</span>
          <span className="kg-summary-divider" />
          <span className="kg-summary-stat kg-summary-published">
            <span className="kg-summary-dot dot-published" />
            Published: <strong>{publishedCount}</strong>
          </span>
          <span className="kg-summary-stat kg-summary-unpublished">
            <span className="kg-summary-dot dot-unpublished" />
            Unpublished: <strong>{unpublishedCount}</strong>
          </span>
        </div>
      )}

      {versions.length === 0 ? (
        <div className="empty-state">
          <p>No versions found.</p>
          <button className="tab-btn tab-btn--primary" onClick={() => setShowCreateModal(true)}>Create First Version</button>
        </div>
      ) : filteredVersions.length === 0 ? (
        <div className="empty-state"><p>No versions match your search.</p></div>
      ) : (
        <div className="versions-table-wrapper">
          <table className="versions-table">
            <colgroup>
              <col style={{ width: '150px' }} />
              <col style={{ width: '200px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '160px' }} />
              <col style={{ width: '150px' }} />
              <col style={{ width: '90px' }} />
              <col style={{ width: '240px' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Version</th>
                <th>Description</th>
                <th>Data Version</th>
                <th>Created At</th>
                <th>Files</th>
                <th>Auto Scale</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVersions.map((version) => (
                <tr key={version.version_id} className="vt-row">

                  {/* Version + status badge */}
                  <td className="vt-ver-cell">
                    <div className="vt-ver-row">
                      <span className="vc-ver-num">v{version.version_id}</span>
                      <span className={`status-badge ${version.published ? 'published' : 'unpublished'}`}>
                        {version.published ? 'Published' : 'Unpublished'}
                      </span>
                    </div>
                    {version.gds_enabled && (
                      <span className="gds-chip">✓ GDS Enabled</span>
                    )}
                  </td>

                  {/* Description — inline editable */}
                  <td className="vt-desc-cell">
                    {editingVersionId === version.version_id ? (
                      <div className="vc-desc-edit">
                        <textarea
                          className="description-textarea"
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          placeholder="Enter description"
                          autoFocus
                          rows={2}
                        />
                        <div className="vc-desc-edit-actions">
                          <button className="tab-btn tab-btn--primary" onClick={() => handleSaveDescription(version.version_id)}>Save</button>
                          <button className="tab-btn tab-btn--ghost" onClick={handleCancelEditDescription}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="vc-desc-wrap">
                        <span className={`vc-desc-text${version.kg_description ? '' : ' empty'}`}>
                          {version.kg_description || 'No description'}
                        </span>
                        <button
                          className="vc-edit-btn"
                          onClick={() => handleStartEditDescription(version.version_id, version.kg_description || '')}
                          title="Edit description"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Data Version — inline editable */}
                  <td className="vt-desc-cell">
                    {editingDataVersionId === version.version_id ? (
                      <div className="vc-desc-edit">
                        <input
                          className="vc-dv-input"
                          type="text"
                          value={editedDataVersion}
                          onChange={(e) => setEditedDataVersion(e.target.value)}
                          placeholder="e.g. v1.0, 2024-Q3"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveDataVersion(version.version_id);
                            if (e.key === 'Escape') handleCancelEditDataVersion();
                          }}
                        />
                        {dataVersionError[version.version_id] && (
                          <div className="vc-dv-error">{dataVersionError[version.version_id]}</div>
                        )}
                        <div className="vc-desc-edit-actions">
                          <button className="tab-btn tab-btn--primary" onClick={() => handleSaveDataVersion(version.version_id)}>Save</button>
                          <button className="tab-btn tab-btn--ghost" onClick={handleCancelEditDataVersion}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="vc-desc-wrap">
                        <span className={`vc-dv-text${version.data_version ? '' : ' empty'}`}>
                          {version.data_version || '—'}
                        </span>
                        <button
                          className="vc-edit-btn"
                          onClick={() => handleStartEditDataVersion(version.version_id, version.data_version || '')}
                          title="Edit data version"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Created At — full date + time */}
                  <td className="vt-date-cell">
                    {formatDateTime(version.created_at)}
                  </td>

                  {/* Files */}
                  <td className="vt-files-cell">
                    <div className="vc-meta-stats">
                      {version.data_files && (
                        <>
                          <span className="vc-meta-stat">
                            <span className="vc-meta-stat-val">{version.data_files.nodes?.length || 0}</span>
                            <span className="vc-meta-stat-label">nodes</span>
                          </span>
                          <span className="vc-meta-stat-sep">·</span>
                          <span className="vc-meta-stat">
                            <span className="vc-meta-stat-val">{version.data_files.edges?.length || 0}</span>
                            <span className="vc-meta-stat-label">edges</span>
                          </span>
                          <span className="vc-meta-stat-sep">·</span>
                        </>
                      )}
                      <span className="vc-meta-stat">
                        <span className="vc-meta-stat-val">{indexCounts[version.version_id] ?? '—'}</span>
                        <span className="vc-meta-stat-label">indexes</span>
                      </span>
                    </div>
                  </td>

                  {/* Auto Scale toggle */}
                  <td className="vt-scale-cell">
                    {versionsWithInstances.has(String(version.version_id)) ? (
                      <label className="autoscale-toggle" title="Toggle auto scaling">
                        <input
                          type="checkbox"
                          checked={version.auto_scaling_enabled ?? false}
                          disabled={autoScalingToggling[version.version_id] ?? false}
                          onChange={() => handleAutoScalingToggle(version.version_id, version.auto_scaling_enabled ?? false)}
                        />
                        <span className="autoscale-slider" />
                      </label>
                    ) : (
                      <span className="autoscale-no-instance" title="Launch an instance first to enable auto scaling">—</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="vt-actions-cell">
                    <div className="vt-actions-cell-inner">
                      <button className="tab-btn tab-btn--ghost" onClick={() => navigate(`/kg/${kgId}/version/${version.version_id}`)}>
                        View Details
                      </button>
                      <button
                        className={version.published ? 'vc-publish-btn-unpublish' : 'vc-publish-btn-publish'}
                        onClick={() => handlePublishToggle(version.version_id, version.published)}
                      >
                        {version.published ? 'Unpublish' : 'Publish'}
                      </button>
                      <button className="tab-btn tab-btn--danger" onClick={() => handleDeleteVersion(version.version_id)}>
                        Delete
                      </button>
                    </div>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && kgId && (
        <CreateVersionModal
          kgId={kgId}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleVersionCreated}
        />
      )}

      {confirmDeleteVersionId && (
        <ConfirmModal
          title="Delete Version"
          message="Are you sure you want to delete this version? This action cannot be undone."
          confirmLabel="Delete"
          danger
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDeleteVersionId(null)}
        />
      )}
    </div>
  );
};

export default KGDetails;
